import React, { useState, useMemo } from 'react';
import { 
  Search, FileSpreadsheet, CheckCircle2, ArrowRight, AlertTriangle, 
  HelpCircle, ExternalLink, ShieldCheck, Calendar, Layers, Wallet, 
  ShoppingBag, Sparkles, ChevronRight, Info, Check, ArrowLeft, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function HelpCenterPage() {
  const [selectedOperator, setSelectedOperator] = useState('bin');
  const [activeTab, setActiveTab] = useState('reconcile'); // 'sales', 'receipts', 'reconcile'
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all'); // 'all', 'single-file', 'separate-files', 'd-plus-one'

  // Banco de Dados de Conhecimento das Operadoras
  const operators = useMemo(() => [
    {
      id: 'bin',
      name: 'Bin (Fiserv)',
      badge: 'Agrupar Repasse',
      badgeColor: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
      category: 'separate-files',
      description: 'Requer atenção na Conciliação Bancária: sempre marcar a caixa de agrupamento por dia e tipo.',
      highlight: 'Na Conciliação Bancária, o lojista DEVE marcar a caixa "Agrupar repasses por dia e tipo (Crédito/Débito)".',
      sales: {
        whereToGet: 'No portal da Bin, acesse Relatórios de Vendas e exporte o período desejado em Excel (.xlsx) ou CSV.',
        systemButton: 'Lançar Vendas (Aba Vendas na Tela Principal)',
        buttonColor: 'bg-blue-600',
        howItWorks: 'O sistema lê a data da venda, bandeira, modalidade (crédito/débito) e o valor bruto transacionado.',
        tips: 'Certifique-se de que o arquivo contém o código de autorização e o comprovante de venda.'
      },
      receipts: {
        whereToGet: 'No portal da Bin, acesse Relatórios Financeiros ou Extrato de Pagamentos.',
        systemButton: 'Lançar Recebimentos (Aba Recebimentos na Tela Principal)',
        buttonColor: 'bg-emerald-600',
        howItWorks: 'O sistema importa as datas de liquidação e o valor líquido após desconto das taxas da máquina.',
        tips: 'Se houver vendas parceladas, o recebimento trará o desmembramento de cada parcela.'
      },
      reconcile: {
        isSpecial: true,
        specialNotice: 'REGRA CRÍTICA PARA OPERADORA BIN: MARQUE A CAIXA DE AGRUPAMENTO',
        specialText: 'O relatório da Bin detalha cada recebimento em linhas separadas. Porém, na sua conta bancária, o banco faz um depósito único somado por dia para Crédito e outro para Débito. Se você não marcar a caixinha "Agrupar repasses por dia e tipo", os valores individuais não vão bater com o valor único do extrato bancário!',
        whereToGetRepasse: 'Exporte o Relatório de Pagamentos Consolidados ou Repasses no portal Bin.',
        whereToGetExtrato: 'Exporte o extrato da sua conta bancária em formato CSV ou Excel no Internet Banking.',
        stepAction: 'No card "Enviar Repasse", marque a caixa "[x] Agrupar repasses por dia e tipo (Crédito/Débito)" antes de enviar a planilha.'
      }
    },
    {
      id: 'sicredi',
      name: 'Sicredi',
      badge: 'Arquivos Separados',
      badgeColor: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      category: 'separate-files',
      description: 'Gera dois arquivos diferentes no portal: um para Vendas e outro para Pagamentos/Recebimentos.',
      highlight: 'Importe separadamente: Comprovante de Venda em "Lançar Vendas" e Comprovante de Pagamento em "Lançar Recebimentos".',
      sales: {
        whereToGet: 'No portal Sicredi Cartões, acesse Relatórios > Comprovante de Venda e baixe o arquivo em Excel (.xlsx).',
        systemButton: 'Lançar Vendas (Aba Vendas)',
        buttonColor: 'bg-blue-600',
        howItWorks: 'O sistema lê a data da venda, valor bruto, bandeira, modalidade e código do pedido. Converte automaticamente a bandeira "MDS" em "Mastercard". Só importa registros com Código do Pedido preenchido.',
        tips: 'O sistema suporta tanto o formato antigo quanto o formato novo da Sicredi (com a coluna "Modalidade").'
      },
      receipts: {
        whereToGet: 'No portal Sicredi Cartões, acesse Relatórios > Comprovante da Venda / Pagamentos.',
        systemButton: 'Lançar Recebimentos (Aba Recebimentos)',
        buttonColor: 'bg-emerald-600',
        howItWorks: 'O sistema lê a "Data de pagamento" e o "Líquido da venda" (já com dedução do MDR). Ele sabe que este é o arquivo de recebimento porque procura pelo termo "Desconto MDR".',
        tips: 'Mesmo que as duas planilhas pareçam parecidas, o sistema identifica automaticamente qual é qual pelo cabeçalho.'
      },
      reconcile: {
        isSpecial: false,
        whereToGetRepasse: 'Baixe o relatório de Pagamentos da Sicredi.',
        whereToGetExtrato: 'Baixe o extrato da sua conta corrente Sicredi (ou de outro banco) em CSV ou Excel.',
        stepAction: 'Envie o repasse no card da esquerda e o extrato bancário no card da direita para conferir os créditos.'
      }
    },
    {
      id: 'pagseguro',
      name: 'PagSeguro (PagBank)',
      badge: 'Arquivo Único (1 Arquivo, 2 Envios)',
      badgeColor: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
      category: 'single-file',
      description: 'O portal PagBank gera um único relatório CSV com todas as informações de vendas e recebimentos juntas.',
      highlight: 'Envie o MESMO arquivo CSV em "Lançar Vendas" e depois em "Lançar Recebimentos". O sistema faz a separação inteligente.',
      sales: {
        whereToGet: 'No portal PagBank, vá em Extratos e Relatórios > Relatório de Vendas/Transações e baixe em CSV.',
        systemButton: 'Lançar Vendas (Aba Vendas)',
        buttonColor: 'bg-blue-600',
        howItWorks: 'Ao enviar no botão de Vendas, o sistema lê a coluna "DATA DA TRANSACAO" e o "VALOR BRUTO".',
        tips: 'O sistema descarta vendas via PIX automaticamente (pois são conciliadas pelo banco) e converte vendas "à vista" ou "pré-pago" para parcela 1.'
      },
      receipts: {
        whereToGet: 'Utilize o MESMO arquivo CSV que você acabou de baixar para as vendas.',
        systemButton: 'Lançar Recebimentos (Aba Recebimentos)',
        buttonColor: 'bg-emerald-600',
        howItWorks: 'Ao enviar o mesmo arquivo no botão de Recebimentos, o sistema muda de comportamento: agora ele lê a coluna "DATA PREVISTA DE LIBERACAO" e o "VALOR LIQUIDO".',
        tips: 'Você não precisa editar o CSV ou apagar colunas. O sistema sabe exatamente quais colunas ler em cada botão!'
      },
      reconcile: {
        isSpecial: false,
        whereToGetRepasse: 'Utilize o relatório de repasses do PagBank.',
        whereToGetExtrato: 'Utilize o extrato bancário em CSV da conta onde o saldo foi transferido.',
        stepAction: 'Faça o cruzamento para validar se os valores líquidos liberados foram devidamente creditados.'
      }
    },
    {
      id: 'mercadopago',
      name: 'Mercado Pago',
      badge: 'Arquivo Único (1 Arquivo, 2 Envios)',
      badgeColor: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
      category: 'single-file',
      description: 'A planilha do Mercado Pago contém os dados da compra e os dados da liberação do dinheiro no mesmo arquivo.',
      highlight: 'Envie a mesma planilha do Mercado Pago nos dois botões. O sistema separa o bruto do líquido automaticamente.',
      sales: {
        whereToGet: 'No portal Mercado Pago, acesse Relatórios > Todas as transações e baixe em CSV ou Excel.',
        systemButton: 'Lançar Vendas (Aba Vendas)',
        buttonColor: 'bg-blue-600',
        howItWorks: 'O sistema extrai a "DATA DA COMPRA (DATECREATED)" e o "VALOR DO PRODUTO (TRANSACTIONAMOUNT)". Descarta rejeitados e PIX.',
        tips: 'Mapeia bandeiras como Mastercard, Visa e Elo automaticamente a partir dos dados do arquivo.'
      },
      receipts: {
        whereToGet: 'Utilize exatamente a mesma planilha do Mercado Pago.',
        systemButton: 'Lançar Recebimentos (Aba Recebimentos)',
        buttonColor: 'bg-emerald-600',
        howItWorks: 'O sistema agora busca a "DATA DE LIBERACAO DO DINHEIRO (DATERELEASED)" e o "VALOR TOTAL RECEBIDO (NETRECEIVEDAMOUNT)".',
        tips: 'Assim você tem a conciliação exata entre o que o cliente comprou e o que o Mercado Pago liberou na sua conta.'
      },
      reconcile: {
        isSpecial: false,
        whereToGetRepasse: 'Utilize o relatório de repasses do Mercado Pago.',
        whereToGetExtrato: 'Utilize o extrato bancário para conferir os saques ou o extrato de conta Mercado Pago.',
        stepAction: 'Cruze os depósitos bancários contra as liberações da plataforma.'
      }
    },
    {
      id: 'cappta',
      name: 'Cappta',
      badge: 'Arquivo Único + D+1',
      badgeColor: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
      category: 'single-file',
      description: 'Um único arquivo com ID, NSU, VALOR e VALORLIQUIDO. O recebimento é calculado com regra D+1.',
      highlight: 'Mesmo arquivo nos dois botões. No recebimento, o sistema projeta o pagamento somando D+1 na data da venda.',
      sales: {
        whereToGet: 'No portal da Cappta, em Relatórios de Captura, exporte a planilha de transações.',
        systemButton: 'Lançar Vendas (Aba Vendas)',
        buttonColor: 'bg-blue-600',
        howItWorks: 'O sistema lê a "DATADECAPTURA" e o "VALOR" bruto. Filtra apenas transações com status "sucesso".',
        tips: 'Usa o ID único da Cappta como autorização e pedido para garantir o batimento perfeito.'
      },
      receipts: {
        whereToGet: 'Utilize a mesma planilha exportada da Cappta.',
        systemButton: 'Lançar Recebimentos (Aba Recebimentos)',
        buttonColor: 'bg-emerald-600',
        howItWorks: 'O sistema lê o "VALORLIQUIDO" e aplica a regra de D+1 (data de captura + 1 dia) para prever a data de pagamento.',
        tips: 'Economiza tempo: com um único download você abastece Vendas e Recebimentos.'
      },
      reconcile: {
        isSpecial: false,
        whereToGetRepasse: 'Planilha de liquidação da Cappta.',
        whereToGetExtrato: 'Extrato da conta bancária de liquidação.',
        stepAction: 'Cruze os valores previstos em D+1 com os depósitos do banco.'
      }
    },
    {
      id: 'tuna',
      name: 'Tuna Pagamentos',
      badge: 'D+1 com Feriados Febraban',
      badgeColor: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
      category: 'd-plus-one',
      description: 'Simulação D+1 dias úteis com motor de feriados nacionais bancários da Febraban.',
      highlight: 'Na Conciliação Bancária, envie o arquivo de Vendas Tuna. O sistema calcula fins de semana e feriados móveis.',
      sales: {
        whereToGet: 'No painel Tuna, exporte o relatório de transações/vendas finalizadas.',
        systemButton: 'Lançar Vendas (se desejar registrar no dashboard)',
        buttonColor: 'bg-blue-600',
        howItWorks: 'Lê data, valor e método de pagamento. Filtra status "Finalizado".',
        tips: 'Muito utilizado para transações Pix e checkout online.'
      },
      receipts: {
        whereToGet: 'A Tuna não exige arquivo separado de recebimento.',
        systemButton: 'Lançar Recebimentos',
        buttonColor: 'bg-emerald-600',
        howItWorks: 'O sistema projeta a data de pagamento baseado no D+1 útil.',
        tips: 'Utilize preferencialmente o fluxo de Conciliação Bancária.'
      },
      reconcile: {
        isSpecial: true,
        specialNotice: 'CALENDÁRIO INTELIGENTE FEBRABAN ATIVO',
        specialText: 'A Tuna deposita em D+1 dias úteis bancários. O sistema possui um algoritmo com calendário oficial que calcula feriados fixos e móveis (Carnaval, Páscoa, Corpus Christi). Se você vendeu numa sexta de Carnaval, o sistema prevê a queda exatamente na quarta de cinzas.',
        whereToGetRepasse: 'Envie o arquivo de Vendas Tuna no card "Enviar Repasse".',
        whereToGetExtrato: 'Envie o extrato da sua conta bancária no card "Enviar Extrato".',
        stepAction: 'O sistema filtra no extrato bancário as linhas que contêm "tuna" e faz o match automático!'
      }
    },
    {
      id: 'ifood',
      name: 'iFood',
      badge: 'Despesas Automáticas',
      badgeColor: 'bg-rose-500/20 text-rose-300 border-rose-500/30',
      category: 'separate-files',
      description: 'Importação inteligente com separação automática de comissões, taxas e saldos de promoção.',
      highlight: 'Saldos negativos de taxas ou cancelamentos vão direto para "Despesas iFood" sem sujar suas vendas.',
      sales: {
        whereToGet: 'No Portal do Parceiro iFood, vá em Vendas e baixe o relatório de vendas do período.',
        systemButton: 'Lançar Vendas (Aba Vendas)',
        buttonColor: 'bg-blue-600',
        howItWorks: 'Aceita vendas concluídas, confirmadas, despachadas e cancelamentos parciais. Descarta pagamentos presenciais (dinheiro e maquininha da loja) e vales de terceiros (como Sodexo), aceitando vouchers do iFood.',
        tips: 'Bandeira fixa "IFood" e tipo "Crédito".'
      },
      receipts: {
        whereToGet: 'No Portal do Parceiro iFood, acesse Financeiro > Repasses e baixe o relatório de recebimentos.',
        systemButton: 'Lançar Recebimentos (Aba Recebimentos)',
        buttonColor: 'bg-emerald-600',
        howItWorks: 'O sistema agrupa por código de pedido e soma linhas com IMPACTO NO REPASSE = SIM. Se o saldo for positivo, vira recebimento. Se for negativo (taxas), vira registro automático na tela "Despesas iFood". Se for positivo mas pago na loja (promoção do iFood), vai para "Saldos Extras".',
        tips: 'Taxas avulsas como mensalidade e pacote de anúncios são lançadas como despesas extras automaticamente.'
      },
      reconcile: {
        isSpecial: true,
        specialNotice: 'AGRUPAMENTO POR LOTE (ID SALDO) E TAXAS ÓRFÃS',
        specialText: 'O iFood agrupa repasses por lote (id_saldo). Se houver uma taxa avulsa sem pedido, o sistema exibe um modal perguntando em qual lote descontar ou se deseja pular.',
        whereToGetRepasse: 'Arquivo de repasses financeiros do iFood.',
        whereToGetExtrato: 'Extrato bancário da conta onde o iFood deposita.',
        stepAction: 'Gera o valor líquido exato de cada lote diário para bater contra o crédito no banco.'
      }
    },
    {
      id: 'getnet',
      name: 'Getnet',
      badge: 'Planilha Multi-Abas',
      badgeColor: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
      category: 'separate-files',
      description: 'Planilha Excel (XLSX) dividida em abas (Cartões, Pix, Detalhado, etc.).',
      highlight: 'O sistema localiza sozinho a aba "Detalhado" nos recebimentos e a aba "Cartões" nas vendas.',
      sales: {
        whereToGet: 'No portal Getnet, exporte o extrato de vendas em formato Excel (.xlsx).',
        systemButton: 'Lançar Vendas (Aba Vendas)',
        buttonColor: 'bg-blue-600',
        howItWorks: 'O parser lê a aba "CARTÕES", descartando transações negadas ou desfeitas e capturando autorização e comprovante.',
        tips: 'Não precisa salvar como CSV nem apagar as outras abas; o sistema processa o XLSX original.'
      },
      receipts: {
        whereToGet: 'No portal Getnet, exporte o extrato de recebíveis/pagamentos em Excel (.xlsx).',
        systemButton: 'Lançar Recebimentos (Aba Recebimentos)',
        buttonColor: 'bg-emerald-600',
        howItWorks: 'O sistema vasculha o arquivo e processa a aba interna chamada "Detalhado", capturando o valor líquido de cada parcela.',
        tips: 'Filtra apenas lançamentos de vendas líquidas, ignorando tarifas avulsas que possam poluir o saldo.'
      },
      reconcile: {
        isSpecial: false,
        whereToGetRepasse: 'Relatório de repasses Getnet.',
        whereToGetExtrato: 'Extrato bancário da conta de liquidação.',
        stepAction: 'Cruze os repasses líquidos contra os depósitos do banco.'
      }
    },
    {
      id: 'cielo_stone_sipag_unica',
      name: 'Cielo, Stone, Sipag e Única',
      badge: 'Adquirentes Tradicionais',
      badgeColor: 'bg-slate-500/20 text-slate-300 border-slate-500/30',
      category: 'separate-files',
      description: 'Lógicas específicas para operadoras consolidadas de maquininha física.',
      highlight: 'Arquivos de Vendas e Recebimentos separados, com tratamento para StoneCode, NSU repetido e parcelas.',
      sales: {
        whereToGet: 'Exporte o relatório de vendas no portal da Cielo, Stone, Sipag ou Única.',
        systemButton: 'Lançar Vendas (Aba Vendas)',
        buttonColor: 'bg-blue-600',
        howItWorks: 'Cielo: lê NSU/DOC e trata cabeçalhos longos. Stone: usa o StoneCode. Sipag: filtra linha "Total". Única: conta NSUs repetidos para numerar parcelas (_1, _2).',
        tips: 'Sempre exporte no formato Excel (.xlsx) ou CSV sem alterar a ordem das colunas.'
      },
      receipts: {
        whereToGet: 'Exporte o extrato financeiro ou de pagamentos no portal correspondente.',
        systemButton: 'Lançar Recebimentos (Aba Recebimentos)',
        buttonColor: 'bg-emerald-600',
        howItWorks: 'Captura data de pagamento e valor líquido. Na Stone, normaliza centavos dividindo por 100. Na Única, lê a coluna Parcela (ex: "1 de 2") para casar com a venda.',
        tips: 'Garante que vendas parceladas casem perfeitamente com os depósitos nos meses seguintes.'
      },
      reconcile: {
        isSpecial: false,
        whereToGetRepasse: 'Relatório de pagamentos/liquidações da maquininha.',
        whereToGetExtrato: 'Extrato bancário da conta corrente.',
        stepAction: 'Cruze os repasses com os depósitos do extrato para verificar divergências de taxa.'
      }
    }
  ], []);

  // Filtragem de operadoras por busca e categoria
  const filteredOperators = useMemo(() => {
    return operators.filter(op => {
      const matchSearch = op.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          op.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          op.badge.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchCategory = filterCategory === 'all' || op.category === filterCategory;
      return matchSearch && matchCategory;
    });
  }, [operators, searchQuery, filterCategory]);

  const currentOp = useMemo(() => {
    return operators.find(o => o.id === selectedOperator) || operators[0];
  }, [operators, selectedOperator]);

  return (
    <div className="min-h-screen bg-[#070C18] text-slate-100 flex flex-col font-sans antialiased selection:bg-emerald-500/30 selection:text-emerald-200">
      
      {/* HEADER PRINCIPAL */}
      <header className="border-b border-slate-800/80 bg-slate-950/80 backdrop-blur-md sticky top-0 z-30 px-4 sm:px-8 py-4">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-emerald-500 to-cyan-500 flex items-center justify-center font-bold text-slate-950 shadow-lg shadow-emerald-500/20 text-lg">
              CC
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold text-white tracking-tight">Central de Ajuda & Guia de Importação</h1>
                <span className="text-[11px] bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-2.5 py-0.5 rounded-full font-semibold">
                  Manual Oficial do Usuário
                </span>
              </div>
              <p className="text-xs text-slate-400">Instruções práticas, regras de arquivos e passo a passo por operadora</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button 
              onClick={() => window.close()}
              variant="outline" 
              size="sm"
              className="border-slate-700 bg-slate-900/60 hover:bg-slate-800 text-slate-300 text-xs flex items-center gap-1.5"
            >
              <ArrowLeft className="w-4 h-4" />
              Fechar Guia
            </Button>
          </div>
        </div>
      </header>

      {/*  */}
      <section className="bg-slate-900/40 border-b border-slate-800/60 px-4 sm:px-8 py-5">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-4 items-center justify-between">
          
          {/*  */}
          <div className="relative w-full md:w-96">
            <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input 
              type="text" 
              placeholder="Buscar operadora (ex: Bin, Sicredi, PagSeguro, D+1...)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-slate-950/80 border border-slate-700/80 rounded-xl pl-10 pr-4 py-2 text-xs text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all"
            />
          </div>

          {/*  */}
          <div className="flex items-center gap-1.5 flex-wrap w-full md:w-auto text-xs">
            <span className="text-slate-500 text-[11px] mr-1 hidden lg:inline">Filtrar por tipo:</span>
            {[
              { id: 'all', label: 'Todas as Operadoras' },
              { id: 'separate-files', label: 'Arquivos Separados' },
              { id: 'single-file', label: 'Arquivo Único (1 Arquivo, 2 Envios)' },
              { id: 'd-plus-one', label: 'D+1 Febraban' }
            ].map(cat => (
              <button
                key={cat.id}
                onClick={() => setFilterCategory(cat.id)}
                className={`px-3 py-1.5 rounded-lg font-medium transition-all ${
                  filterCategory === cat.id 
                    ? 'bg-emerald-500 text-slate-950 font-bold shadow-sm shadow-emerald-500/20' 
                    : 'bg-slate-900/60 text-slate-400 hover:text-white border border-slate-800'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/*  */}
      <main className="max-w-7xl mx-auto w-full flex-1 px-4 sm:px-8 py-8 flex flex-col lg:flex-row gap-8">
        
        {/*  */}
        <aside className="w-full lg:w-80 shrink-0 space-y-2.5">
          <div className="flex items-center justify-between text-xs font-semibold text-slate-400 px-1 mb-2">
            <span>SELECIONE A OPERADORA</span>
            <span className="text-[10px] bg-slate-800 px-2 py-0.5 rounded-md text-slate-300">
              {filteredOperators.length} disponíveis
            </span>
          </div>

          <div className="space-y-1.5">
            {filteredOperators.map(op => {
              const isSelected = op.id === selectedOperator;
              return (
                <button
                  key={op.id}
                  onClick={() => setSelectedOperator(op.id)}
                  className={`w-full text-left p-3.5 rounded-xl border transition-all flex items-center justify-between group ${
                    isSelected
                      ? 'bg-gradient-to-r from-emerald-500/15 to-transparent border-emerald-500/50 shadow-md shadow-emerald-500/5'
                      : 'bg-slate-900/40 border-slate-800/80 hover:bg-slate-800/40 hover:border-slate-700'
                  }`}
                >
                  <div className="space-y-1 pr-2">
                    <div className="flex items-center gap-2">
                      <span className={`font-semibold text-sm ${isSelected ? 'text-emerald-400' : 'text-slate-200 group-hover:text-white'}`}>
                        {op.name}
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-400 line-clamp-1">{op.description}</p>
                    <span className={`inline-block text-[10px] px-2 py-0.5 rounded font-medium border ${op.badgeColor}`}>
                      {op.badge}
                    </span>
                  </div>
                  <ChevronRight className={`w-4 h-4 shrink-0 transition-transform ${isSelected ? 'text-emerald-400 translate-x-1' : 'text-slate-600 group-hover:text-slate-400'}`} />
                </button>
              );
            })}

            {filteredOperators.length === 0 && (
              <div className="p-6 text-center text-slate-500 text-xs border border-dashed border-slate-800 rounded-xl">
                Nenhuma operadora encontrada para a busca "{searchQuery}".
              </div>
            )}
          </div>

          {/*  */}
          <div className="p-4 rounded-xl bg-slate-900/60 border border-slate-800/80 text-xs text-slate-400 space-y-2 mt-6">
            <div className="flex items-center gap-1.5 text-amber-400 font-semibold text-xs">
              <Sparkles className="w-4 h-4" />
              <span>Como o sistema pensa:</span>
            </div>
            <p className="text-[11px] leading-relaxed">
              O conciliador não adivinha dados: ele cruza o <strong>Código de Autorização</strong>, <strong>Código do Pedido</strong> ou <strong>NSU</strong> com precisão cirúrgica de centavos.
            </p>
          </div>
        </aside>

        {/*  */}
        <section className="flex-1 space-y-6">
          
          {/*  */}
          <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6 shadow-xl relative overflow-hidden">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="space-y-2 max-w-2xl">
                <div className="flex items-center gap-2.5">
                  <h2 className="text-2xl font-bold text-white tracking-tight">{currentOp.name}</h2>
                  <span className={`text-xs px-2.5 py-0.5 rounded-full font-semibold border ${currentOp.badgeColor}`}>
                    {currentOp.badge}
                  </span>
                </div>
                <p className="text-xs text-slate-300 leading-relaxed">{currentOp.description}</p>
              </div>
            </div>

            {/*  */}
            <div className="mt-4 p-3.5 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-200 text-xs flex items-start gap-2.5">
              <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
              <div>
                <strong className="text-amber-300 font-semibold">Regra de Ouro desta operadora: </strong>
                <span>{currentOp.highlight}</span>
              </div>
            </div>
          </div>

          {/*  */}
          <div className="flex items-center gap-2 border-b border-slate-800 pb-2">
            {[
              { id: 'sales', label: '1. Como Lançar Vendas', icon: ShoppingBag, color: 'text-blue-400' },
              { id: 'receipts', label: '2. Como Lançar Recebimentos', icon: Wallet, color: 'text-emerald-400' },
              { id: 'reconcile', label: '3. Conciliação Bancária', icon: Layers, color: 'text-cyan-400' }
            ].map(tab => {
              const isActive = activeTab === tab.id;
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold transition-all ${
                    isActive 
                      ? 'bg-slate-800 text-white shadow-sm border border-slate-700' 
                      : 'text-slate-400 hover:text-slate-200 hover:bg-slate-900/60'
                  }`}
                >
                  <Icon className={`w-4 h-4 ${tab.color}`} />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/*  */}
          {activeTab === 'sales' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                
                {/*  */}
                <div className="p-5 rounded-2xl bg-slate-900/50 border border-slate-800 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="w-7 h-7 rounded-lg bg-blue-500/20 text-blue-400 font-bold text-xs flex items-center justify-center">
                      1
                    </span>
                    <span className="text-[10px] text-slate-500 font-mono">PORTAL OPERADORA</span>
                  </div>
                  <h4 className="font-semibold text-sm text-white">Baixar o Extrato</h4>
                  <p className="text-xs text-slate-400 leading-relaxed">{currentOp.sales.whereToGet}</p>
                </div>

                {/*  */}
                <div className="p-5 rounded-2xl bg-slate-900/50 border border-slate-800 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="w-7 h-7 rounded-lg bg-blue-500/20 text-blue-400 font-bold text-xs flex items-center justify-center">
                      2
                    </span>
                    <span className="text-[10px] text-slate-500 font-mono">NO CONCILIADOR</span>
                  </div>
                  <h4 className="font-semibold text-sm text-white">Onde Clicar</h4>
                  <div className="p-2.5 rounded-lg bg-blue-600/20 border border-blue-500/30 text-blue-300 text-xs font-semibold flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-blue-400" />
                    {currentOp.sales.systemButton}
                  </div>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    Clique no botão azul de Lançar Vendas na tela inicial.
                  </p>
                </div>

                {/*  */}
                <div className="p-5 rounded-2xl bg-slate-900/50 border border-slate-800 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="w-7 h-7 rounded-lg bg-blue-500/20 text-blue-400 font-bold text-xs flex items-center justify-center">
                      3
                    </span>
                    <span className="text-[10px] text-slate-500 font-mono">INTELIGÊNCIA</span>
                  </div>
                  <h4 className="font-semibold text-sm text-white">O que o sistema faz</h4>
                  <p className="text-xs text-slate-400 leading-relaxed">{currentOp.sales.howItWorks}</p>
                </div>
              </div>

              {/*  */}
              <div className="p-4 rounded-xl bg-slate-900/80 border border-slate-800 text-xs text-slate-300 flex items-start gap-3">
                <Info className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
                <div>
                  <strong className="text-white">Dica do desenvolvedor:</strong> {currentOp.sales.tips}
                </div>
              </div>
            </div>
          )}

          {/*  */}
          {activeTab === 'receipts' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                
                {/*  */}
                <div className="p-5 rounded-2xl bg-slate-900/50 border border-slate-800 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="w-7 h-7 rounded-lg bg-emerald-500/20 text-emerald-400 font-bold text-xs flex items-center justify-center">
                      1
                    </span>
                    <span className="text-[10px] text-slate-500 font-mono">ARQUIVO DE REPASSE</span>
                  </div>
                  <h4 className="font-semibold text-sm text-white">Arquivo a Utilizar</h4>
                  <p className="text-xs text-slate-400 leading-relaxed">{currentOp.receipts.whereToGet}</p>
                </div>

                {/*  */}
                <div className="p-5 rounded-2xl bg-slate-900/50 border border-slate-800 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="w-7 h-7 rounded-lg bg-emerald-500/20 text-emerald-400 font-bold text-xs flex items-center justify-center">
                      2
                    </span>
                    <span className="text-[10px] text-slate-500 font-mono">NO CONCILIADOR</span>
                  </div>
                  <h4 className="font-semibold text-sm text-white">Onde Clicar</h4>
                  <div className="p-2.5 rounded-lg bg-emerald-600/20 border border-emerald-500/30 text-emerald-300 text-xs font-semibold flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    {currentOp.receipts.systemButton}
                  </div>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    Clique no botão verde de Lançar Recebimentos na tela inicial.
                  </p>
                </div>

                {/*  */}
                <div className="p-5 rounded-2xl bg-slate-900/50 border border-slate-800 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="w-7 h-7 rounded-lg bg-emerald-500/20 text-emerald-400 font-bold text-xs flex items-center justify-center">
                      3
                    </span>
                    <span className="text-[10px] text-slate-500 font-mono">CONCILIAÇÃO</span>
                  </div>
                  <h4 className="font-semibold text-sm text-white">Como é processado</h4>
                  <p className="text-xs text-slate-400 leading-relaxed">{currentOp.receipts.howItWorks}</p>
                </div>
              </div>

              {/*  */}
              <div className="p-4 rounded-xl bg-slate-900/80 border border-slate-800 text-xs text-slate-300 flex items-start gap-3">
                <Info className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                <div>
                  <strong className="text-white">Dica de liquidação:</strong> {currentOp.receipts.tips}
                </div>
              </div>
            </div>
          )}

          {/*  */}
          {activeTab === 'reconcile' && (
            <div className="space-y-6">

              {/*  */}
              {currentOp.reconcile.isSpecial && (
                <div className="p-5 rounded-2xl bg-gradient-to-r from-amber-500/15 via-emerald-500/10 to-transparent border border-amber-500/30 space-y-2">
                  <div className="flex items-center gap-2 text-amber-300 font-bold text-xs tracking-wide">
                    <AlertTriangle className="w-4 h-4 text-amber-400" />
                    {currentOp.reconcile.specialNotice}
                  </div>
                  <p className="text-xs text-slate-300 leading-relaxed">
                    {currentOp.reconcile.specialText}
                  </p>
                </div>
              )}

              {/*  */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                
                {/*  */}
                <div className="p-5 rounded-2xl bg-slate-900/60 border border-slate-800 space-y-3 relative overflow-hidden">
                  <div className="flex items-center gap-2 text-emerald-400 font-semibold text-sm">
                    <FileSpreadsheet className="w-4 h-4" />
                    Card 1: Enviar Repasse
                  </div>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    {currentOp.reconcile.whereToGetRepasse}
                  </p>

                  {/* SIMULAÇÃO DA CAIXINHA DE SELEÇÃO QUANDO FOR BIN */}
                  {currentOp.id === 'bin' && (
                    <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 text-xs space-y-1">
                      <div className="flex items-center gap-2 text-amber-200 font-semibold">
                        <input type="checkbox" checked readOnly className="w-4 h-4 rounded text-emerald-500 bg-slate-950 border-emerald-500 cursor-default" />
                        <span>Agrupar repasses por dia e tipo (Crédito/Débito)</span>
                      </div>
                      <p className="text-[10px] text-amber-300/80">
                        * Obrigatório deixar marcado para bater com o depósito consolidado da conta.
                      </p>
                    </div>
                  )}

                  <div className="text-[11px] text-emerald-300/90 bg-emerald-500/10 p-2.5 rounded-lg border border-emerald-500/20">
                    <strong>Ação:</strong> {currentOp.reconcile.stepAction}
                  </div>
                </div>

                {/*  */}
                <div className="p-5 rounded-2xl bg-slate-900/60 border border-slate-800 space-y-3 relative overflow-hidden">
                  <div className="flex items-center gap-2 text-cyan-400 font-semibold text-sm">
                    <FileSpreadsheet className="w-4 h-4" />
                    Card 2: Enviar Extrato Bancário
                  </div>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    {currentOp.reconcile.whereToGetExtrato}
                  </p>
                  <div className="text-[11px] text-cyan-300/90 bg-cyan-500/10 p-2.5 rounded-lg border border-cyan-500/20">
                    <strong>Formatos aceitos:</strong> CSV ou Excel exportado diretamente do Internet Banking (Sicredi, Sicoob, Santander, etc.).
                  </div>
                </div>

              </div>

              {/*  */}
              <div className="p-4 rounded-xl bg-slate-900/80 border border-slate-800 text-xs text-slate-300 flex items-start gap-3">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                <div>
                  <strong className="text-white">Resultado após o cruzamento:</strong> O sistema exibirá o total conciliado, o valor pendente e as tabelas com os repasses encontrados e não encontrados, permitindo ainda baixar o relatório final em PDF.
                </div>
              </div>
            </div>
          )}

          {/*  */}
          <div className="border-t border-slate-800/80 pt-6 space-y-3">
            <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Perguntas Frequentes & Resolução de Dúvidas</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
              <div className="p-3.5 rounded-xl bg-slate-900/40 border border-slate-800/80 space-y-1">
                <p className="font-semibold text-slate-200">Como sei se o arquivo é Venda ou Recebimento?</p>
                <p className="text-slate-400 text-[11px] leading-relaxed">
                  Para operadoras de arquivo separado (Sicredi/Cielo), o arquivo de Vendas foca no que passou na maquininha, enquanto o Recebimento foca na data que o dinheiro cai no banco com as taxas descontadas.
                </p>
              </div>

              <div className="p-3.5 rounded-xl bg-slate-900/40 border border-slate-800/80 space-y-1">
                <p className="font-semibold text-slate-200">E se eu enviar o arquivo errado na tela?</p>
                <p className="text-slate-400 text-[11px] leading-relaxed">
                  O sistema possui validação inteligente. Se você tentar enviar uma planilha de recebimentos no botão de vendas, ele alertará que o cabeçalho não corresponde e impedirá o erro.
                </p>
              </div>
            </div>
          </div>

        </section>

      </main>

      {/*  */}
      <footer className="border-t border-slate-800/80 bg-slate-950/80 px-4 sm:px-8 py-4 text-center text-xs text-slate-500">
        Conciliador de Cartões SaaS • Guia de Uso e Importação de Extratos para Clientes
      </footer>

    </div>
  );
}
